import { createFileRoute, notFound, redirect } from '@tanstack/react-router'
import { authRepository } from '@/data/adapters/api/auth.api'
import { requireAuth } from '@/lib/auth-guard'
import { orgLandingTarget } from '@/lib/org-landing'

/**
 * Slug-free deep links into this app's agency-wide panels.
 *
 * The shell links to `/go/overview`, not `/$orgSlug/overview`, because the
 * shell has no idea what anyone's workspace slug is — that lives behind
 * `/auth/workspaces`, which is this app's business. Rather than teach the shell
 * to resolve slugs (a second implementation of a rule that already exists here,
 * and one more thing to keep in step), the app resolves its own.
 *
 * These panels are agency-wide concerns that happen to be rendered inside this
 * app. When one genuinely moves to the shell, its entry here goes away.
 */
const PANELS = {
  overview: 'overview',
  prospects: 'prospects',
  'plan-defaults': 'plan-defaults',
} as const

type Panel = keyof typeof PANELS

export const Route = createFileRoute('/go/$panel')({
  beforeLoad: async ({ params }) => {
    await requireAuth()

    if (!(params.panel in PANELS)) throw notFound()
    const panel = params.panel as Panel

    const ws = await authRepository.getWorkspaces()
    const landing = orgLandingTarget(ws)

    // No workspace to hang the panel off — send them where the app would have
    // sent them anyway rather than to a route that cannot resolve.
    if (landing.to === '/welcome' || landing.to === '/select') throw redirect(landing)

    const orgSlug = landing.params.orgSlug
    if (panel === 'overview') {
      throw redirect({ to: '/$orgSlug/overview', params: { orgSlug } })
    }
    if (panel === 'prospects') {
      throw redirect({ to: '/$orgSlug/prospects', params: { orgSlug } })
    }
    throw redirect({ to: '/$orgSlug/plan-defaults', params: { orgSlug } })
  },
})
